/**
 * TouchControls - Phase 4 Touch-Friendly Components
 *
 * Provides touch-optimized controls that meet WCAG accessibility
 * requirements and improve mobile user experience.
 *
 * Features:
 * - TouchableArea: Enforces minimum 44px touch targets
 * - SwipeGesture: Swipe gesture detection
 * - PullToRefresh: Pull-to-refresh interaction
 * - TouchFeedback: Visual feedback for touch interactions
 *
 * WCAG Requirements:
 * - Minimum touch target size: 44x44 CSS pixels
 * - Adequate spacing between targets: 8px
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

// ============================================
// Constants
// ============================================

export const TOUCH_CONFIG = {
  MIN_TARGET_SIZE: 44, // WCAG 2.1 minimum
  MIN_SPACING: 8,
  SWIPE_THRESHOLD: 50, // pixels
  SWIPE_VELOCITY_THRESHOLD: 0.3, // pixels/ms
  LONG_PRESS_DURATION: 500, // ms
  RIPPLE_DURATION: 400, // ms
};

// ============================================
// TouchableArea Component
// ============================================

/**
 * TouchableArea - Wrapper ensuring minimum touch target size
 *
 * Wraps content to guarantee WCAG-compliant touch target sizes.
 * Automatically expands hit area while maintaining visual appearance.
 *
 * @example
 * <TouchableArea onPress={() => console.log('pressed')}>
 *   <SmallIcon />
 * </TouchableArea>
 */
export const TouchableArea = forwardRef(({
  children,
  onPress,
  onLongPress,
  minSize = TOUCH_CONFIG.MIN_TARGET_SIZE,
  disabled = false,
  feedback = true,
  feedbackColor = 'rgba(139, 92, 246, 0.2)',
  className = '',
  style = {},
  as: Component = 'button',
  ...props
}, ref) => {
  const [isPressed, setIsPressed] = useState(false);
  const [ripples, setRipples] = useState([]);
  const touchStartRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const containerRef = useRef(null);

  // Combine refs
  const combinedRef = (node) => {
    containerRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  // Handle touch/mouse start
  const handlePressStart = useCallback((e) => {
    if (disabled) return;

    setIsPressed(true);
    touchStartRef.current = {
      time: Date.now(),
      x: e.clientX ?? e.touches?.[0]?.clientX ?? 0,
      y: e.clientY ?? e.touches?.[0]?.clientY ?? 0,
    };

    // Ripple effect
    if (feedback && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
      const y = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;

      const ripple = {
        id: Date.now(),
        x,
        y,
        size: Math.max(rect.width, rect.height) * 2,
      };

      setRipples((prev) => [...prev, ripple]);

      // Remove ripple after animation
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== ripple.id));
      }, TOUCH_CONFIG.RIPPLE_DURATION);
    }

    // Long press detection
    if (onLongPress) {
      longPressTimerRef.current = setTimeout(() => {
        onLongPress(e);
      }, TOUCH_CONFIG.LONG_PRESS_DURATION);
    }
  }, [disabled, feedback, onLongPress]);

  // Handle touch/mouse end
  const handlePressEnd = useCallback((e) => {
    if (disabled) return;

    setIsPressed(false);

    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // Trigger press if not a long press
    if (touchStartRef.current) {
      const duration = Date.now() - touchStartRef.current.time;
      if (duration < TOUCH_CONFIG.LONG_PRESS_DURATION && onPress) {
        onPress(e);
      }
    }

    touchStartRef.current = null;
  }, [disabled, onPress]);

  // Cancel on touch move (prevents accidental presses)
  const handlePressCancel = useCallback(() => {
    setIsPressed(false);
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const containerStyle = useMemo(() => ({
    minWidth: minSize,
    minHeight: minSize,
    position: 'relative',
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    ...style,
  }), [minSize, disabled, style]);

  return (
    <Component
      ref={combinedRef}
      className={`
        touch-none select-none
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
        ${isPressed ? 'scale-95' : 'scale-100'}
        transition-transform duration-150
        ${className}
      `}
      style={containerStyle}
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressCancel}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressCancel}
      disabled={disabled}
      aria-disabled={disabled}
      type={Component === 'button' ? 'button' : undefined}
      {...props}
    >
      {/* Ripple effects */}
      {feedback && ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full pointer-events-none animate-ping"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
            backgroundColor: feedbackColor,
            animationDuration: `${TOUCH_CONFIG.RIPPLE_DURATION}ms`,
          }}
        />
      ))}
      {children}
    </Component>
  );
});

TouchableArea.displayName = 'TouchableArea';

TouchableArea.propTypes = {
  children: PropTypes.node.isRequired,
  onPress: PropTypes.func,
  onLongPress: PropTypes.func,
  minSize: PropTypes.number,
  disabled: PropTypes.bool,
  feedback: PropTypes.bool,
  feedbackColor: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  as: PropTypes.elementType,
};

// ============================================
// SwipeGesture Component
// ============================================

/**
 * SwipeGesture - Detects swipe gestures
 *
 * Wraps content to detect and handle swipe gestures
 * in any direction.
 *
 * @example
 * <SwipeGesture
 *   onSwipeLeft={() => console.log('swiped left')}
 *   onSwipeRight={() => console.log('swiped right')}
 * >
 *   <CardContent />
 * </SwipeGesture>
 */
export const SwipeGesture = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  onSwipe,
  threshold = TOUCH_CONFIG.SWIPE_THRESHOLD,
  disabled = false,
  className = '',
  ...props
}) => {
  const touchStartRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, [disabled]);

  const handleTouchEnd = useCallback((e) => {
    if (disabled || !touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const duration = Date.now() - touchStartRef.current.time;

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const velocity = Math.max(absX, absY) / duration;

    // Must exceed threshold and be primarily in one direction
    if (Math.max(absX, absY) >= threshold || velocity >= TOUCH_CONFIG.SWIPE_VELOCITY_THRESHOLD) {
      let direction = null;

      if (absX > absY) {
        // Horizontal swipe
        direction = deltaX > 0 ? 'right' : 'left';
        if (direction === 'left' && onSwipeLeft) {
          onSwipeLeft({ deltaX, deltaY, velocity });
        } else if (direction === 'right' && onSwipeRight) {
          onSwipeRight({ deltaX, deltaY, velocity });
        }
      } else {
        // Vertical swipe
        direction = deltaY > 0 ? 'down' : 'up';
        if (direction === 'up' && onSwipeUp) {
          onSwipeUp({ deltaX, deltaY, velocity });
        } else if (direction === 'down' && onSwipeDown) {
          onSwipeDown({ deltaX, deltaY, velocity });
        }
      }

      if (direction && onSwipe) {
        onSwipe({ direction, deltaX, deltaY, velocity });
      }
    }

    touchStartRef.current = null;
  }, [disabled, threshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onSwipe]);

  return (
    <div
      className={className}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      {...props}
    >
      {children}
    </div>
  );
};

SwipeGesture.propTypes = {
  children: PropTypes.node.isRequired,
  onSwipeLeft: PropTypes.func,
  onSwipeRight: PropTypes.func,
  onSwipeUp: PropTypes.func,
  onSwipeDown: PropTypes.func,
  onSwipe: PropTypes.func,
  threshold: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

// ============================================
// PullToRefresh Component
// ============================================

/**
 * PullToRefresh - Pull-to-refresh interaction
 *
 * Adds pull-to-refresh behavior to scrollable content.
 *
 * @example
 * <PullToRefresh onRefresh={async () => await fetchData()}>
 *   <ScrollableList />
 * </PullToRefresh>
 */
export const PullToRefresh = ({
  children,
  onRefresh,
  threshold = 80,
  refreshingHeight = 60,
  disabled = false,
  className = '',
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);

  const handleScroll = useCallback((e) => {
    setIsAtTop(e.target.scrollTop <= 0);
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing || !isAtTop) return;

    touchStartRef.current = {
      y: e.touches[0].clientY,
      scrollTop: containerRef.current?.scrollTop || 0,
    };
  }, [disabled, isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e) => {
    if (disabled || isRefreshing || !touchStartRef.current || !isAtTop) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartRef.current.y;

    if (deltaY > 0 && touchStartRef.current.scrollTop <= 0) {
      // Pulling down at top of scroll
      e.preventDefault();
      // Apply resistance
      setPullDistance(Math.min(deltaY * 0.5, threshold * 1.5));
    }
  }, [disabled, isRefreshing, isAtTop, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (disabled || isRefreshing) return;

    if (pullDistance >= threshold && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(refreshingHeight);

      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }

    touchStartRef.current = null;
  }, [disabled, isRefreshing, pullDistance, threshold, refreshingHeight, onRefresh]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-auto ${className}`}
      onScroll={handleScroll}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center transition-all duration-200"
        style={{
          top: -refreshingHeight,
          height: refreshingHeight,
          transform: `translateY(${pullDistance}px)`,
        }}
      >
        {isRefreshing ? (
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent" />
        ) : (
          <div
            className="transition-transform duration-200"
            style={{
              transform: pullDistance >= threshold ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        )}
      </div>

      {/* Content with pull transform */}
      <div
        className="transition-transform duration-200"
        style={{
          transform: `translateY(${pullDistance}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

PullToRefresh.propTypes = {
  children: PropTypes.node.isRequired,
  onRefresh: PropTypes.func.isRequired,
  threshold: PropTypes.number,
  refreshingHeight: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

// ============================================
// TouchFeedback Component
// ============================================

/**
 * TouchFeedback - Visual feedback for touch interactions
 *
 * Provides visual feedback when an element is touched,
 * similar to material ripple effect.
 *
 * @example
 * <TouchFeedback>
 *   <MenuItem>Option 1</MenuItem>
 * </TouchFeedback>
 */
export const TouchFeedback = ({
  children,
  color = 'rgba(139, 92, 246, 0.2)',
  duration = TOUCH_CONFIG.RIPPLE_DURATION,
  disabled = false,
  className = '',
}) => {
  const [ripples, setRipples] = useState([]);
  const containerRef = useRef(null);

  const handleInteraction = useCallback((e) => {
    if (disabled || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
    const y = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;
    const size = Math.max(rect.width, rect.height) * 2;

    const ripple = { id: Date.now(), x, y, size };
    setRipples((prev) => [...prev, ripple]);

    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== ripple.id));
    }, duration);
  }, [disabled, duration]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onMouseDown={handleInteraction}
      onTouchStart={handleInteraction}
    >
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
            backgroundColor: color,
            animation: `ripple ${duration}ms ease-out forwards`,
          }}
        />
      ))}
      {children}
      <style>{`
        @keyframes ripple {
          from {
            transform: scale(0);
            opacity: 1;
          }
          to {
            transform: scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

TouchFeedback.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
  duration: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

// ============================================
// useTouchGesture Hook
// ============================================

/**
 * useTouchGesture - Hook for custom touch gesture handling
 */
export const useTouchGesture = (options = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onLongPress,
    onPinch,
    threshold = TOUCH_CONFIG.SWIPE_THRESHOLD,
    longPressDelay = TOUCH_CONFIG.LONG_PRESS_DURATION,
  } = options;

  const touchesRef = useRef([]);
  const longPressTimerRef = useRef(null);
  const startDistanceRef = useRef(null);

  const handlers = useMemo(() => ({
    onTouchStart: (e) => {
      touchesRef.current = Array.from(e.touches).map((t) => ({
        x: t.clientX,
        y: t.clientY,
        time: Date.now(),
      }));

      // Long press detection
      if (e.touches.length === 1 && onLongPress) {
        longPressTimerRef.current = setTimeout(() => {
          onLongPress(e);
        }, longPressDelay);
      }

      // Pinch detection setup
      if (e.touches.length === 2 && onPinch) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        startDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
      }
    },

    onTouchMove: (e) => {
      // Cancel long press on move
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      // Pinch gesture
      if (e.touches.length === 2 && onPinch && startDistanceRef.current) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const currentDistance = Math.sqrt(dx * dx + dy * dy);
        const scale = currentDistance / startDistanceRef.current;
        onPinch({ scale, distance: currentDistance });
      }
    },

    onTouchEnd: (e) => {
      // Clear long press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      // Swipe detection
      if (touchesRef.current.length === 1 && e.changedTouches.length === 1) {
        const start = touchesRef.current[0];
        const end = e.changedTouches[0];
        const deltaX = end.clientX - start.x;
        const deltaY = end.clientY - start.y;

        if (Math.abs(deltaX) >= threshold || Math.abs(deltaY) >= threshold) {
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX > 0 && onSwipeRight) onSwipeRight();
            if (deltaX < 0 && onSwipeLeft) onSwipeLeft();
          } else {
            if (deltaY > 0 && onSwipeDown) onSwipeDown();
            if (deltaY < 0 && onSwipeUp) onSwipeUp();
          }
        }
      }

      touchesRef.current = [];
      startDistanceRef.current = null;
    },
  }), [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onLongPress, onPinch, threshold, longPressDelay]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  return handlers;
};

// ============================================
// Exports
// ============================================

export default TouchableArea;
